import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogService } from '../../../services/catalogService';
import { CatalogAccessMode } from '../../../types/catalog';
import type { Product } from '../../../types/product';
import {
  getCreateCatalogDraftKey,
  getEditCatalogDraftKey,
  clearCreateCatalogDraft,
  clearEditCatalogDraft,
} from '../../../utils/catalogDraftStorage';

interface CatalogLineItemForm {
  productId: number;
  productName?: string;
  productCode?: string;
  unitPrice: string;
}

interface CatalogDraftState {
  name: string;
  accessMode: CatalogAccessMode;
  lineItems: CatalogLineItemForm[];
  accessEmails: string[];
}

interface UseCatalogEditorParams {
  companyId: number | null;
  catalogId: number | null;
}

export function useCatalogEditor({ companyId, catalogId }: UseCatalogEditorParams) {
  const navigate = useNavigate();

  // Navigating to the product picker and back unmounts this page, which would otherwise
  // wipe every field already entered — persist the in-progress draft to sessionStorage,
  // the same pattern useActivityGroupData uses for the Activity create/edit form.
  const storageKey = companyId
    ? (catalogId ? getEditCatalogDraftKey(companyId, catalogId) : getCreateCatalogDraftKey(companyId))
    : null;

  const loadDraft = (): CatalogDraftState | null => {
    if (!storageKey) return null;
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return null;
    try {
      return JSON.parse(saved) as CatalogDraftState;
    } catch (err) {
      console.error('Failed to parse saved catalog draft:', err);
      return null;
    }
  };

  // Lazy initializer — runs once on mount, not on every render
  const [initialDraft] = useState(loadDraft);

  const clearDraft = () => {
    if (!companyId) return;
    if (catalogId) {
      clearEditCatalogDraft(companyId, catalogId);
    } else {
      clearCreateCatalogDraft(companyId);
    }
  };

  const [name, setName] = useState(initialDraft?.name ?? '');
  const [accessMode, setAccessMode] = useState<CatalogAccessMode>(initialDraft?.accessMode ?? CatalogAccessMode.PUBLIC);
  const [lineItems, setLineItems] = useState<CatalogLineItemForm[]>(initialDraft?.lineItems ?? []);
  const [accessEmails, setAccessEmails] = useState<string[]>(initialDraft?.accessEmails ?? []);

  const [loading, setLoading] = useState(!!catalogId && !initialDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing catalog in edit mode — skipped when a draft already covers it (e.g.
  // returning from the product picker)
  useEffect(() => {
    if (!catalogId || initialDraft) return;

    let cancelled = false;

    const fetchCatalog = async () => {
      setLoading(true);
      try {
        const catalog = await catalogService.getById(catalogId);
        if (cancelled) return;
        setName(catalog.name);
        setAccessMode(catalog.accessMode);
        setLineItems((catalog.lineItems ?? []).map((item) => ({
          productId: item.productId,
          productName: item.productName,
          productCode: item.productCode,
          unitPrice: String(item.unitPrice),
        })));
        setAccessEmails(catalog.accessEmails ?? []);
      } catch (err) {
        console.error('Error fetching catalog:', err);
        if (!cancelled) {
          setError('Не вдалося завантажити каталог');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCatalog();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogId]);

  // Persist the draft on every change
  useEffect(() => {
    if (!storageKey) return;
    const draft: CatalogDraftState = { name, accessMode, lineItems, accessEmails };
    sessionStorage.setItem(storageKey, JSON.stringify(draft));
  }, [storageKey, name, accessMode, lineItems, accessEmails]);

  const handleProductsSelected = (products: Product[]) => {
    setLineItems((prev) => {
      const existingIds = new Set(prev.map((item) => item.productId));
      const newItems = products
        .filter((product) => !existingIds.has(product.id))
        .map((product) => ({
          productId: product.id,
          productName: product.name,
          productCode: product.code,
          unitPrice: '',
        }));
      return [...prev, ...newItems];
    });
  };

  const handleRemoveLineItem = (productId: number) => {
    setLineItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const handleUnitPriceChange = (productId: number, unitPrice: string) => {
    setLineItems((prev) => prev.map((item) => (
      item.productId === productId ? { ...item, unitPrice } : item
    )));
  };

  const handleAddAccessEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || accessEmails.includes(trimmed)) return;
    setAccessEmails((prev) => [...prev, trimmed]);
  };

  const handleRemoveAccessEmail = (email: string) => {
    setAccessEmails((prev) => prev.filter((e) => e !== email));
  };

  const hasValidPrices = lineItems.every((item) => {
    const price = Number(item.unitPrice);
    return item.unitPrice.trim().length > 0 && !Number.isNaN(price) && price > 0;
  });

  const isValid = name.trim().length > 0
    && lineItems.length > 0
    && hasValidPrices
    && (accessMode !== CatalogAccessMode.RESTRICTED || accessEmails.length > 0);

  const handleSubmit = async () => {
    if (!companyId || !isValid) return;

    setSaving(true);
    setError(null);
    try {
      const requestPayload = {
        name: name.trim(),
        accessMode,
        lineItems: lineItems.map((item) => ({
          productId: item.productId,
          unitPrice: Number(item.unitPrice),
        })),
        accessEmails: accessMode === CatalogAccessMode.RESTRICTED ? accessEmails : [],
      };

      if (catalogId) {
        await catalogService.update(catalogId, requestPayload);
      } else {
        await catalogService.create({ companyId, ...requestPayload });
      }

      clearDraft();
      navigate(`/companies/${companyId}/catalogs`);
    } catch (err) {
      console.error('Error saving catalog:', err);
      setError('Не вдалося зберегти каталог');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    clearDraft();
    navigate(`/companies/${companyId}/catalogs`);
  };

  return {
    data: {
      loading,
      saving,
      error,
    },
    form: {
      name,
      setName,
      accessMode,
      setAccessMode,
      lineItems,
      accessEmails,
    },
    actions: {
      handleProductsSelected,
      handleRemoveLineItem,
      handleUnitPriceChange,
      handleAddAccessEmail,
      handleRemoveAccessEmail,
      handleSubmit,
      handleCancel,
    },
    isValid,
  };
}
